/**
 * @file: RoomOrderCard.tsx
 *
 * Order details on the room-detail screen for an order-internal group. Resolves
 * the order via the existing order search (by orderNo) and renders a card with
 * the same information the task list's TaskCardV2 shows — vehicle header, info
 * grid, status, and the report-progress row (报告进度 / 车主查看情况). The
 * per-stage "viewed" state is fetched dynamically (same hook the task list uses).
 *
 * Renders the full card layout IMMEDIATELY (no spinner) to avoid layout shift:
 * the plate comes from the room snapshot up front; values not yet loaded show a
 * `--` placeholder; the report row shows a skeleton (like TaskCardV2) until the
 * order resolves. Read-only.
 */

import { DashedSeparator } from '@euler/app/components/task-card/components/DashedSeparator';
import { FuelTypeChip } from '@euler/app/components/task-card/components/FuelTypeChip';
import { InfoCell } from '@euler/app/components/task-card/components/InfoCell';
import { ModelBadge } from '@euler/app/components/task-card/components/ModelBadge';
import { ProgressConnector } from '@euler/app/components/task-card/components/ProgressConnector';
import { ReportProgressStep } from '@euler/app/components/task-card/components/ReportProgressStep';
import { SkeletonBar } from '@euler/app/components/task-card/components/SkeletonBar';
import { StatusBadgeV2 } from '@euler/app/components/task-card/components/StatusBadgeV2';
import { SubscriptionChip } from '@euler/app/components/task-card/components/SubscriptionChip';
import { TagScrollView } from '@euler/app/components/task-card/components/TagScrollView';
import {
  TOKENS,
  kLogoSize,
  kStepCircleSize,
  kStepColumnWidth,
  kStepKeys,
  kStepLabels,
} from '@euler/app/components/task-card/constants';
import { useReportShareViewSummary } from '@euler/app/components/task-card/hooks';
import {
  computeInShopDuration,
  computeVehicleAge,
  deriveStatusBadge,
  formatMileage,
} from '@euler/app/components/task-card/utils';
import { Typography } from '@euler/app/components/typography';
import { Avatar } from '@euler/components/Avatar';
import { Img } from '@euler/components/image/AdvancedImage';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { taskStatusFromOrderStatus } from '@euler/model';
import { CommonTaskStatus, ReceptionOrderStatus } from '@euler/model/enum';
import { SearchOrderResultItem } from '@euler/model/order/SearchOrderResultItem';
import { parseTagsFromString } from '@euler/utils';
import { formatDateTime } from '@euler/utils/formatters';
import { Gauge } from 'lucide-react-native';
import { FC, Fragment, memo } from 'react';
import { View } from 'react-native';

const PH = '--';

export const RoomOrderCard: FC<{
  order: SearchOrderResultItem | null;
  /** Plate from the room snapshot — shown immediately, before the order loads. */
  licensePlateNo: string | null;
}> = memo(({ order, licensePlateNo }) => {
  const { rpx } = useResponsiveContext();
  const { summary, loading } = useReportShareViewSummary(order?.orderNo);

  const hasOrder = order != null;
  const plate = order?.licensePlateNo ?? licensePlateNo ?? PH;
  const statusBadge = order
    ? deriveStatusBadge({
        isRollback: order.isRollback ?? false,
        status: taskStatusFromOrderStatus(order.status),
        isMainTask: order.isMainOrder,
        hasChildTasks: order.hasChildOrders,
        preInspectionStatus: order.preInspectionStatus,
        inspectionStatus: order.inspectionStatus,
        constructionStatus: order.constructionStatus,
        isConstructionJobScheduled: order.isConstructionJobScheduled,
        deliveryCheckStatus: order.deliveryCheckStatus,
      })
    : null;
  const tags = order
    ? Array.isArray(order.tags)
      ? (order.tags as string[])
      : parseTagsFromString(order.tags ?? '')
    : [];
  const age = order
    ? computeVehicleAge(order.vehicleModelYear ?? undefined)
    : null;
  const finished = order?.status === ReceptionOrderStatus.Finished;
  const subscribed = (order?.subscriptions?.length ?? 0) > 0;
  const historyMode = order?.finishedAt != null;

  const stepStatuses = {
    preInspection: order?.preInspectionStatus,
    inspection: order?.inspectionStatus,
    construction: order?.constructionStatus,
    deliveryCheck: order?.deliveryCheckStatus,
  } as const;

  return (
    <View
      css={`
        background-color: ${TOKENS.cardBg};
        margin-bottom: ${rpx(8)}px;
      `}
    >
      {/* HEADER */}
      <View
        css={`
          padding: ${rpx(12)}px ${rpx(14)}px;
          position: relative;
          min-height: ${rpx(58 + 24)}px;
          justify-content: center;
        `}
      >
        <Img
          uri={order?.vehicleImageUrl}
          source={
            order?.vehicleImageUrl
              ? undefined
              : require('@euler/assets/img/default-car-img.png')
          }
          css={`
            position: absolute;
            right: ${rpx(14)}px;
            top: ${rpx(12)}px;
            width: ${rpx(96)}px;
            height: ${rpx(58)}px;
            z-index: 0;
          `}
        />
        <View>
          <HStack justifyContent="flex-start" alignItems="center">
            {order?.vehicleBrandLogo ? (
              <Img
                uri={order.vehicleBrandLogo}
                css={`
                  width: ${rpx(kLogoSize)}px;
                  height: ${rpx(kLogoSize)}px;
                  flex-shrink: 0;
                `}
              />
            ) : (
              <Avatar
                name={order?.vehicleName ?? plate}
                css={`
                  width: ${rpx(kLogoSize)}px;
                  height: ${rpx(kLogoSize)}px;
                  flex-shrink: 0;
                  border-radius: ${rpx(kLogoSize)}px;
                `}
                textStyle={{ fontSize: rpx(12) }}
              />
            )}
            <Typography.Label
              bold
              size={rpx(22)}
              lineHeight={rpx(28)}
              numberOfLines={1}
              letterSpacing={rpx(-0.5)}
              color={TOKENS.textPrimary}
              css={`
                margin-left: ${rpx(8)}px;
                font-family: System;
              `}
            >
              {plate}
            </Typography.Label>
            {order?.vehicleModel && order.vehicleModel !== '未知' ? (
              <ModelBadge label={order.vehicleModel} />
            ) : null}
          </HStack>
          <HStack
            justifyContent="flex-start"
            alignItems="center"
            css={`
              margin-top: ${rpx(6)}px;
              padding-left: ${rpx(kLogoSize + 8)}px;
            `}
          >
            <FuelTypeChip
              fuelType={
                hasOrder
                  ? order!.vehicleFuelType?.trim() &&
                    order!.vehicleFuelType.trim() !== '未知'
                    ? order!.vehicleFuelType.trim()
                    : '汽油'
                  : PH
              }
            />
            <Typography.Label
              size={rpx(11)}
              lineHeight={rpx(15)}
              color={TOKENS.textMuted}
              css={`
                margin-left: ${rpx(8)}px;
              `}
            >
              车龄{' '}
              <Typography.Text medium size={rpx(11)} color={TOKENS.textPrimary}>
                {hasOrder ? (age != null ? `${age}年` : '-') : PH}
              </Typography.Text>
            </Typography.Label>
            <Typography.Label
              size={rpx(11)}
              lineHeight={rpx(15)}
              color={TOKENS.textMuted}
              numberOfLines={1}
              css={`
                margin-left: ${rpx(6)}px;
              `}
            >
              · {order?.vehicleBrandName || PH}
            </Typography.Label>
          </HStack>
        </View>
      </View>

      <DashedSeparator />

      {/* INFO GRID */}
      <View
        css={`
          padding: ${rpx(10)}px ${rpx(14)}px;
          flex-direction: row;
          flex-wrap: wrap;
        `}
      >
        <InfoCell
          iconNode={
            <Gauge size={rpx(12)} color={TOKENS.textFaint} strokeWidth={2} />
          }
          label="行驶里程"
          value={hasOrder ? formatMileage(order!.vehicleMileage) : PH}
        />
        <InfoCell
          icon="clock"
          label={finished ? '耗时' : '在厂'}
          value={
            hasOrder
              ? computeInShopDuration(order!.createdAt, order!.finishedAt)
              : PH
          }
        />
        <InfoCell
          icon="user"
          label="服务顾问"
          value={hasOrder ? order!.serviceAgentName : PH}
        />
        <InfoCell
          icon="clipboard"
          label={finished ? '完工' : '建单'}
          value={
            hasOrder
              ? formatDateTime(
                  order!.finishedAt ?? order!.createdAt,
                  'M/d HH:mm',
                )
              : PH
          }
        />
      </View>

      <DashedSeparator />

      {/* STATUS ROW */}
      <HStack
        justifyContent="space-between"
        alignItems="center"
        css={`
          padding: ${rpx(10)}px ${rpx(14)}px ${rpx(12)}px;
        `}
      >
        <HStack
          justifyContent="flex-start"
          css={`
            flex: 1;
            overflow: hidden;
          `}
        >
          {statusBadge ? (
            <StatusBadgeV2
              label={statusBadge.label}
              palette={statusBadge.palette}
            />
          ) : (
            <StatusBadgeV2 label={PH} palette="gray" />
          )}
          {hasOrder ? <TagScrollView tags={tags} /> : null}
        </HStack>
        {hasOrder ? <SubscriptionChip subscribed={subscribed} /> : null}
      </HStack>

      {/* REPORT PROGRESS */}
      <View
        css={`
          background-color: ${TOKENS.progressBg};
          border-top-width: ${rpx(1)}px;
          border-top-color: ${TOKENS.divider};
          border-top-style: solid;
          padding: ${rpx(10)}px ${rpx(20)}px;
        `}
      >
        <HStack justifyContent="space-between" alignItems="center">
          <Typography.Label
            size={rpx(10.5)}
            lineHeight={rpx(14)}
            color={TOKENS.textFaint}
          >
            报告进度
          </Typography.Label>
          <Typography.Label
            size={rpx(10.5)}
            lineHeight={rpx(14)}
            color={TOKENS.textFaint}
          >
            车主查看情况
          </Typography.Label>
        </HStack>
        <View
          css={`
            margin-top: ${rpx(8)}px;
            flex-direction: row;
            align-items: flex-start;
          `}
        >
          {kStepKeys.map((key, idx) => (
            <Fragment key={key}>
              {hasOrder ? (
                <ReportProgressStep
                  label={kStepLabels[key]}
                  status={stepStatuses[key]}
                  viewed={summary?.viewSummary?.[key] ?? null}
                  loading={loading}
                  historyMode={historyMode}
                />
              ) : (
                <SkeletonStep label={kStepLabels[key]} />
              )}
              {idx < kStepKeys.length - 1 ? (
                <ProgressConnector
                  active={
                    hasOrder && stepStatuses[key] === CommonTaskStatus.Finished
                  }
                />
              ) : null}
            </Fragment>
          ))}
        </View>
      </View>
    </View>
  );
});

/** A single report-progress step in its loading state — skeleton circle +
 * static stage label + skeleton view-status bar. Matches the loaded step's
 * footprint so nothing shifts when the order resolves. */
const SkeletonStep: FC<{ label: string }> = memo(({ label }) => {
  const { rpx } = useResponsiveContext();
  return (
    <View
      css={`
        align-items: center;
        flex-shrink: 0;
        width: ${rpx(kStepColumnWidth)}px;
      `}
    >
      <SkeletonBar width={rpx(kStepCircleSize)} height={rpx(kStepCircleSize)} />
      <Typography.Label
        size={rpx(10)}
        lineHeight={rpx(14)}
        color={TOKENS.textSecondary}
        css={`
          margin-top: ${rpx(4)}px;
        `}
      >
        {label}
      </Typography.Label>
      <View
        css={`
          margin-top: ${rpx(2)}px;
          height: ${rpx(12)}px;
          justify-content: center;
        `}
      >
        <SkeletonBar width={rpx(36)} height={rpx(8)} />
      </View>
    </View>
  );
});
